import React, { useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

import { User } from '../firebase/entities';

import useFetchUsersAll from '../hooks/getUsersAll';
import useCreateNotification from '../hooks/createdNotification';
import { toast } from 'react-toastify';

const SelectedUsersList: React.FC<{ selectedUsers: any[]; handleUserDeselect: (id: string) => void }> = ({ selectedUsers, handleUserDeselect }) => {
	return (
		<div className="bg-white shadow-md rounded-lg p-4 w-64 max-h-[600px] overflow-y-auto h-[600px]">
			<h3 className="text-lg font-semibold text-gray-800 mb-4">Selected Users</h3>
			<ul className="divide-y divide-gray-200">
				{selectedUsers.map((user) => (
					<li key={user.id} className="flex justify-between items-center py-2">
						<div className="flex flex-wrap">
							<div className="w-full md:w-9/12 p-4">
								<p className="text-sm font-semibold text-gray-700">{user.name}</p>
								<p className="text-xs text-gray-500">{user.email}</p>
							</div>
							<div className='w-full md:w-3/12 p-4'>
								<button
									className="btn btn-sm btn-icon btn-danger mr-2"
									onClick={() => handleUserDeselect(user.id || '')}
								>
									<XMarkIcon className="size-4"/>
								</button>
							</div>
						</div>
					</li>
				))}
			</ul>
		</div>
	);
};

const AlertsPage: React.FC = () => {
	const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [message, setMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
	
	const { createNotification, loading, error } = useCreateNotification();

	const { data, status } = useFetchUsersAll();

	const users = useMemo(() => {
		return (data || []).map((user: User) => user) ?? [];
	}, [data]);

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUserIds([]);
    } else {
      const allUserIds = filteredUsers.map((user) => user.id);

      setSelectedUserIds(allUserIds);
    }

    setSelectAll(!selectAll);
  };

  const handleUserSelect = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
    } else {
      setSelectedUserIds([...selectedUserIds, userId]);
    }
  };

  const handleUserDeselect = (userId: string) => {
    setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
  };

  const handleSubmit = async () => {
    if (!message || selectedUserIds.length <= 0) {
			return toast.warn(`Select users and add a message.`);
    }

		try {
			let sendNotifications = [];

			for (const id of selectedUserIds) {
				sendNotifications.push(createNotification({
					title: `Notification From Admin`,
					description: `${message}`,
					userId: id,
					type: 'home',
					action: '',
					data: {
						id
					}
				}));
			}

			await Promise.all(sendNotifications);

			setSelectedUserIds([]);
			setSearchTerm('');
			setMessage('');
			toast.success(`Notifications sent successfully.`);
		} catch(error) {
			return toast.error(`Error sending notifications.`);
		}
  };

  const filteredUsers = users.filter((user) =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedUsers = users.filter((user) => selectedUserIds.includes(user.id));

  return (
    <div className="container mx-auto my-8 px-6">
			<div className="w-100 block pb-6 border-b-2 border-primary-700 mb-5">
				<h1 className="font-bold text-2xl">Send Notifications</h1>
			</div>

      <div className="flex space-x-6">
        <div>
          <SelectedUsersList selectedUsers={selectedUsers} handleUserDeselect={handleUserDeselect} />
        </div>

        <div className="flex-1 bg-white rounded-lg p-6">
          <textarea
            className="border border-gray-300 rounded-md p-4 w-full mb-6 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your message here..."
          />

          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search for users by name or email..."
              className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center justify-between mb-4">
            <label className="flex items-center text-gray-700 font-medium">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
                className="mr-2 h-5 w-5 text-blue-600 border-gray-300 rounded"
              />
              Select all users
            </label>
            <span className="text-sm text-gray-500">
              {selectedUserIds.length}/{filteredUsers.length} selected
            </span>
          </div>

          <div className="border border-gray-200 rounded-lg h-64 overflow-y-auto mb-6">
            <ul className="divide-y divide-gray-200">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user: any) => (
                  <li key={user.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                    <label className="flex items-center text-gray-700 font-medium">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => handleUserSelect(user.id)}
                        className="mr-3 h-5 w-5 text-blue-600 border-gray-300 rounded"
                      />
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </label>
                  </li>
                ))
              ) : (
                <li className="px-4 py-3 text-gray-500 text-center">No users found</li>
              )}
            </ul>
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSubmit}
              className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition duration-300 ease-in-out"
							disabled={loading}
            >
              Send notification
            </button>
          </div>
        </div>
      </div>
    </div>
	);
};

export default AlertsPage;